import { useState } from 'react';
import { signInWithGoogle, signInWithMicrosoft, sendEmailLink } from '../config/firebase';

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 50%, #2ec4b6 100%)',
    padding: '20px'
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
  },
  logoGroup: {
    textAlign: 'center',
    marginBottom: '36px'
  },
  logoMain: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#0f4c5c',
    letterSpacing: '-0.03em'
  },
  logoSub: {
    fontSize: '10px',
    color: '#8a94a3',
    textTransform: 'uppercase',
    letterSpacing: '4px',
    marginTop: '2px'
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a2332',
    textAlign: 'center',
    marginBottom: '8px'
  },
  subtitle: {
    fontSize: '14px',
    color: '#5a6577',
    textAlign: 'center',
    marginBottom: '32px'
  },
  btn: {
    width: '100%',
    padding: '14px 20px',
    borderRadius: '10px',
    border: '1px solid #e2e6ea',
    background: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'inherit',
    color: '#1a2332',
    transition: 'all 0.15s ease',
    marginBottom: '12px'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '20px 0',
    color: '#8a94a3',
    fontSize: '12px'
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#e2e6ea'
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '10px',
    border: '1px solid #e2e6ea',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    marginBottom: '12px',
    color: '#1a2332'
  },
  emailBtn: {
    width: '100%',
    padding: '14px 20px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit'
  },
  error: {
    color: '#e07a5f',
    fontSize: '13px',
    textAlign: 'center',
    marginBottom: '16px',
    padding: '10px',
    background: 'rgba(224,122,95,0.08)',
    borderRadius: '8px'
  },
  success: {
    color: '#0f4c5c',
    fontSize: '13px',
    textAlign: 'center',
    marginBottom: '16px',
    padding: '10px',
    background: 'rgba(46,196,182,0.08)',
    borderRadius: '8px'
  },
  providerIcon: {
    width: '20px',
    height: '20px'
  }
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoft = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithMicrosoft();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      setError('');
      setLoading(true);
      await sendEmailLink(email.trim());
      setEmailSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoGroup}>
          <div style={styles.logoMain}>trendency</div>
          <div style={styles.logoSub}>research</div>
        </div>

        <div style={styles.title}>Dashboard Access</div>
        <div style={styles.subtitle}>Sign in to view your dashboards</div>

        {error && <div style={styles.error}>{error}</div>}
        {emailSent && <div style={styles.success}>Check your email for a sign-in link!</div>}

        <button
          style={styles.btn}
          onClick={handleGoogle}
          disabled={loading}
          onMouseEnter={(e) => { e.target.style.borderColor = '#2ec4b6'; e.target.style.background = '#f7f8fa'; }}
          onMouseLeave={(e) => { e.target.style.borderColor = '#e2e6ea'; e.target.style.background = '#fff'; }}
        >
          <svg style={styles.providerIcon} viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        {/* Microsoft login — uncomment once Azure app registration is configured
        <button
          style={styles.btn}
          onClick={handleMicrosoft}
          disabled={loading}
          onMouseEnter={(e) => { e.target.style.borderColor = '#2ec4b6'; e.target.style.background = '#f7f8fa'; }}
          onMouseLeave={(e) => { e.target.style.borderColor = '#e2e6ea'; e.target.style.background = '#fff'; }}
        >
          <svg style={styles.providerIcon} viewBox="0 0 24 24">
            <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
            <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
            <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
            <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
          </svg>
          Continue with Microsoft
        </button>
        */}

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span>or sign in with email</span>
          <div style={styles.dividerLine}></div>
        </div>

        <form onSubmit={handleEmailLink}>
          <input
            style={styles.input}
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = '#2ec4b6'; }}
            onBlur={(e) => { e.target.style.borderColor = '#e2e6ea'; }}
          />
          <button
            type="submit"
            style={{
              ...styles.emailBtn,
              opacity: loading ? 0.7 : 1
            }}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send sign-in link'}
          </button>
        </form>
      </div>
    </div>
  );
}
